import { useAtomValue } from 'jotai'
import { useMemo } from 'react'
import useBalance from 'src/common/contracts/hooks/token/useBalance'
import {
  bestOffersAtom,
  buyTokenAmountAtom,
  buyTokenAtom,
  offersAtom,
  sellTokenAmountAtom,
  sellTokenAtom,
  skipOfferToTakeIdsAtom,
} from '../../../stores/trade-store'
import useSmartAccount from 'src/common/api-clients/safe-protocol-kit/useSmartAccount'

const useSwapFormState = () => {
  const {smartAccountInfo} = useSmartAccount()
  const sellToken = useAtomValue(sellTokenAtom)
  const sellTokenAmount = useAtomValue(sellTokenAmountAtom)
  const buyToken = useAtomValue(buyTokenAtom)
  const buyTokenAmount = useAtomValue(buyTokenAmountAtom)

  const { balance, isLoading: balanceLoading } = useBalance({
    token: sellToken,
    owner: smartAccountInfo?.address,
  })

  const enoughFunds = balance && balance.gte(sellTokenAmount)

  const skipOfferToTakeIds = useAtomValue(skipOfferToTakeIdsAtom)
  const offers = useAtomValue(offersAtom)
  const bestOffers = useAtomValue(bestOffersAtom)

  const hasBaseOfferData = buyToken && sellToken && sellTokenAmount.gt(0)
  const hasValidOffer = hasBaseOfferData && buyTokenAmount.gt(0)
  const hasAvailableOffers = hasValidOffer && offers.length > 0

  const isSkipAll =
    skipOfferToTakeIds.length > 0 &&
    bestOffers.every(offer => skipOfferToTakeIds.includes(offer.id))

  const hasOffersToTake = bestOffers.length > 0

  const actionStatusMessage = useMemo(() => {
    if (!buyToken) return 'Select a token'
    if (sellTokenAmount.eq(0) || buyTokenAmount.eq(0)) return 'Enter amount'
    if (isSkipAll) return 'No offers selected'
    if (!hasOffersToTake) return 'Insufficient offers. Place a Limit order'
    if (!enoughFunds) return 'Not enough funds'
    return 'Preview Swap'
  }, [
    buyToken,
    sellTokenAmount,
    buyTokenAmount,
    isSkipAll,
    hasOffersToTake,
    enoughFunds,
  ])

  const isActionDisabled =
    !buyToken ||
    isSkipAll ||
    !hasBaseOfferData ||
    (hasOffersToTake && !enoughFunds)

  return {
    actionStatusMessage,
    loading: balanceLoading,
    isActionDisabled,
    hasAvailableOffers,
    hasOffersToTake,
  }
}

export default useSwapFormState
